import { getCurrentInstance, onBeforeUnmount, ref, watch, type Ref } from 'vue';

export interface TypewriterOptions {
  /** Delay before typing starts. `retarget` is true when a caption is already on
   *  screen and the text changed under it — scrubbing across a row of skills
   *  should settle before re-typing, or the caption strobes. */
  dwellMs?: (retarget: boolean) => number;
  typeMs?: number;
  eraseMs?: number;
  /** Skips the per-character animation, for prefers-reduced-motion. */
  instant?: () => boolean;
}

/**
 * Reveals `source` one character at a time and backspaces it away when the
 * source empties, so a caption reads like a line being typed into a terminal.
 *
 * Driving this off a single string rather than off hover events is deliberate:
 * every caption state (hover hint, hovered-skill preview, armed skill) reduces
 * to "what text should be showing right now", and the transitions between them
 * then fall out for free.
 */
export function useTypewriter(source: Ref<string>, options: TypewriterOptions = {}) {
  const typeMs = options.typeMs ?? 34;
  const eraseMs = options.eraseMs ?? 20;
  const dwellMs = options.dwellMs ?? (() => 400);
  const instant = options.instant ?? (() =>
      typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

  const typed = ref('');
  const open = ref(false);

  let dwellTimer: ReturnType<typeof setTimeout> | null = null;
  let charTimer: ReturnType<typeof setInterval> | null = null;

  const stopTimers = () => {
    if (dwellTimer) clearTimeout(dwellTimer);
    if (charTimer) clearInterval(charTimer);
    dwellTimer = null;
    charTimer = null;
  };

  const typeIn = (full: string) => {
    open.value = true;
    if (instant()) {
      typed.value = full;
      return;
    }
    charTimer = setInterval(() => {
      if (typed.value.length >= full.length) {
        stopTimers();
        return;
      }
      typed.value = full.slice(0, typed.value.length + 1);
    }, typeMs);
  };

  const eraseOut = () => {
    if (!open.value) return;
    if (instant()) {
      typed.value = '';
      open.value = false;
      return;
    }
    charTimer = setInterval(() => {
      if (!typed.value.length) {
        stopTimers();
        open.value = false;
        return;
      }
      typed.value = typed.value.slice(0, -1);
    }, eraseMs);
  };

  watch(source, (next) => {
    const retarget = open.value;
    stopTimers();
    if (!next) {
      eraseOut();
      return;
    }
    // A retarget restarts the line rather than backspacing it first; the caret
    // stays put and the new name types over the old one.
    if (retarget) typed.value = '';
    dwellTimer = setTimeout(() => typeIn(next), dwellMs(retarget));
  }, { immediate: true });

  const stop = () => {
    stopTimers();
    typed.value = '';
    open.value = false;
  };

  if (getCurrentInstance()) onBeforeUnmount(stopTimers);

  return { typed, open, stop };
}
